import { useState, useEffect, useMemo } from 'react';
import { Spinner, Alert, Modal, ModalBody, ModalPill } from '@components/ui';
import {
  CARD,
  CTA,
  GHOST,
  PAGE_BTN,
  ICON_BTN,
  ICON_BTN_DANGER,
  METRIC_LABEL,
  TONE_SURFACE,
  TONE_BAR,
  ROW_HOVER,
} from '@components/ui/designTokens';
import { PlusIcon, PencilIcon, TrashIcon, MagnifyingGlassIcon, XMarkIcon, ArrowUpTrayIcon, CubeIcon } from '@heroicons/react/24/outline';
import { useProducts, useCreateProduct, useUpdateProduct, useDeleteProduct } from '../hooks/useProducts';
import { ProductForm } from './ProductForm';
import { ProductImportModal } from './ProductImportModal';
import type { Product } from '../types/product.types';
// SUBSCRIPTIONS_DISABLED: import { useConsumption } from '@/features/subscriptions/hooks/useConsumption';
import { useQueryClient } from '@tanstack/react-query';
import { Sparkline } from '@components/charts/MiniCharts';
import Swal from 'sweetalert2';
import { useSupportConfig } from '@features/support';

/**
 * Productos — Claude Design · Productos.dc.html
 *
 * El diseño cambia la tabla plana por una "hoja de catálogo": las filas se
 * agrupan bajo un encabezado de categoría con numeración monoespaciada y regla
 * dasheada. No se perdio ninguna columna — SKU, Nombre (+ descripcion), Tipo,
 * Categoria, Precio, Codigo Impuesto, Estado y las dos acciones siguen en cada
 * fila. La categoria queda ademas promovida al encabezado del grupo, que es una
 * segunda lectura, no un reemplazo.
 *
 * Tipo y Estado dejan de ser badges repetidos en todas las filas y pasan a ser
 * punto de color + etiqueta; el chip completo se reserva para "Inactivo", que es
 * el caso excepcional. Es la misma decision que ya tomo el ledger de Clientes
 * para no pintar 24 pastillas en una pantalla.
 *
 * La franja de resumen sale unicamente de los productos de la pagina actual
 * (isService, priceIncludesTax, createdAt). El endpoint responde
 * { products, total, page, limit, totalPages }: no trae conteos por tipo ni por
 * categoria de alcance global. `total` es el unico dato global y es el unico que
 * se muestra sin el rotulo "en esta pagina".
 *
 * Por lo mismo, el agrupamiento por categoria describe la pagina cargada, no el
 * catalogo completo — el backend pagina de a 20 y no expone un endpoint de
 * categorias.
 */

/**
 * Grilla de la hoja: 7 tracks para las 8 columnas del listado original, en el
 * mismo orden. Tipo y Categoria comparten celda (apilados) para ahorrar ancho;
 * los dos datos se siguen mostrando.
 *
 * SOLO Nombre crece (`minmax(0,1fr)`); el resto de los tracks se ajusta a su
 * contenido (`minmax(min, auto)`). Cuando varias columnas llevaban `fr`, el
 * sobrante se repartia entre todas y quedaban huecos enormes entre datos
 * cortos — el ancho libre tiene que ir a la unica columna que lo aprovecha.
 * El `min-w` es el piso a partir del cual el wrapper (overflow-x-auto) empieza
 * a scrollear.
 *
 * Lo que evita que una celda pise a la vecina NO es el ancho del track sino la
 * contencion de cada celda: `min-w-0` + `truncate` para texto corrido,
 * `break-all` para identificadores sin espacios (los SKU de Aludra llegan a ~26
 * caracteres) y quiebre permitido en el precio (admite 12 enteros + 2
 * decimales). Dimensionar los tracks para el peor caso es el error contrario:
 * regala ancho en todas las filas por una sola fila extrema.
 */
const ROW_GRID =
  'grid min-w-[720px] grid-cols-[minmax(96px,auto)_minmax(0,1fr)_minmax(104px,auto)_minmax(92px,auto)_minmax(78px,auto)_minmax(84px,auto)_68px] items-center gap-x-4';

/**
 * Precio: alineado a la derecha para que los decimales queden a plomo columna
 * abajo, con `pr-2` para despegarlo de Codigo Impuesto. Sin `whitespace-nowrap`
 * a proposito: un importe extremo baja de linea dentro de su celda en vez de
 * desbordarse sobre la columna vecina.
 */
const PRICE_CELL = 'min-w-0 pr-2 text-right';

/**
 * Encabezados: sin `whitespace-nowrap`. Los rotulos largos (`CÓDIGO IMPUESTO`,
 * `TIPO · CATEGORÍA`) envuelven en dos lineas en vez de obligar a un track de
 * 144px solo para el titulo. El tracking es mas corto que el resto de la hoja
 * por lo mismo.
 */
const COL_HEAD =
  'text-[11px] font-bold uppercase leading-[1.25] tracking-[0.09em] text-secondary-400';

/** Circunferencia del arco de "precio con impuesto incluido" (r = 24). */
const ARC_LENGTH = 2 * Math.PI * 24;

/** Productos sin `category` (el campo es opcional). */
const UNCATEGORIZED = 'Sin categoría';

export function ProductListPage() {
  const [page, setPage] = useState(1);
  const [searchInput, setSearchInput] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [searchType, setSearchType] = useState<'name' | 'code'>('name');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const queryClient = useQueryClient();

  // Debounce search input: wait 500ms after user stops typing
  useEffect(() => {
    const timer = setTimeout(() => {
      // Only search if there are at least 3 characters or if it's empty (to show all)
      if (searchInput.length === 0 || searchInput.length >= 2) {
        setDebouncedSearch(searchInput);
        setPage(1); // Reset to first page when searching
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchInput]);

  const { data, isLoading, error } = useProducts({
    page,
    // Nota: Aludra ignora el limit y usa su propio (20 por defecto)
    name: searchType === 'name' ? debouncedSearch : undefined,
    code: searchType === 'code' ? debouncedSearch : undefined
  });

  // Debug: Log when page or data changes
  useEffect(() => {
    console.log('📄 Page or data changed:', {
      page,
      dataPage: data?.page,
      productsCount: data?.products.length,
      total: data?.total,
      totalPages: data?.totalPages,
      firstProductSKU: data?.products[0]?.sku,
      lastProductSKU: data?.products[data?.products.length - 1]?.sku,
    });
  }, [page, data]);

  // El backend ya pagina, usamos directamente los productos que devuelve
  // Aludra usa su propio limit (20 por defecto), respetamos lo que devuelve
  const limit = data?.limit || 20;
  const totalPages = data?.totalPages || 1;
  // Memoizado (mismo valor que `data?.products || []`) para que el resumen y la
  // agrupacion de abajo no se recalculen en cada render por una referencia nueva.
  const products = useMemo(() => data?.products ?? [], [data]);

  const createMutation = useCreateProduct();
  /*
   * Con el asistente activo, el fallo del alta ya se muestra en el modal del
   * asistente (lo dispara el onError de la mutacion) CON la causa real y la
   * explicacion. Este Swal generico quedaba encima diciendo "intenta de nuevo",
   * que no aporta nada y obliga a un clic extra justo cuando el cliente esta
   * trabado. Si el asistente esta apagado, se conserva: sin el, el cliente se
   * quedaria sin ningun aviso de que fallo.
   */
  const { assistantEnabled } = useSupportConfig();
  const updateMutation = useUpdateProduct();
  const deleteMutation = useDeleteProduct();
  // SUBSCRIPTIONS_DISABLED: const { validateCanAddProduct } = useConsumption();

  /**
   * Resumen de la pagina actual. Se calcula sobre los productos que ya estan en
   * memoria (los mismos que se listan abajo). No hay consulta extra ni ningun
   * valor estimado.
   */
  const summary = useMemo(() => {
    let asService = 0;
    let withTaxIncluded = 0;
    const byMonth = new Map<string, number>();

    for (const product of products) {
      if (product.isService) asService += 1;
      if (product.priceIncludesTax) withTaxIncluded += 1;

      const created = product.createdAt ? new Date(product.createdAt) : null;
      if (created && !isNaN(created.getTime())) {
        const key = `${created.getFullYear()}-${String(created.getMonth()).padStart(2, '0')}`;
        byMonth.set(key, (byMonth.get(key) ?? 0) + 1);
      }
    }

    // Altas por mes de creacion, en orden cronologico. Con un solo mes la serie
    // tiene un punto y el sparkline no dibuja, que es lo correcto: no hay
    // tendencia que mostrar.
    const altasByMonth = [...byMonth.entries()]
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([, count]) => count);

    return {
      asService,
      asProduct: products.length - asService,
      withTaxIncluded,
      altasByMonth,
    };
  }, [products]);

  const taxIncludedPct = products.length > 0 ? summary.withTaxIncluded / products.length : 0;

  const handleOpenCreate = () => {
    setEditingProduct(null);
    setIsModalOpen(true);
  };

  const handleImportSuccess = () => {
    // Invalidate and refetch products
    queryClient.invalidateQueries({ queryKey: ['products'] });
    Swal.fire({
      title: 'Importación exitosa',
      text: 'Los productos han sido importados correctamente',
      icon: 'success',
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#3085d6',
    });
  };

  const handleOpenEdit = (product: Product) => {
    setEditingProduct(product);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setEditingProduct(null);
  };

  const handleSubmit = async (data: Partial<Product>) => {
    console.log('📝 handleSubmit - Start', { editingProduct, data });

    /* SUBSCRIPTIONS_DISABLED
    if (!editingProduct) {
      const validation = validateCanAddProduct();
      if (!validation.allowed) {
        const result = await Swal.fire({
          title: 'Límite de productos alcanzado',
          text: validation.reason,
          icon: 'warning',
          showCancelButton: true,
          confirmButtonText: 'Ver planes',
          cancelButtonText: 'Cancelar',
          confirmButtonColor: '#3085d6',
        });
        if (result.isConfirmed) {
          window.location.href = '/subscriptions/plans';
        }
        return;
      }
    }
    */

    try {
      if (editingProduct) {
        console.log('📝 Actualizando producto...');
        await updateMutation.mutateAsync({ id: editingProduct.id, data });
        handleCloseModal();

        // Success message for update
        Swal.fire({
          title: '¡Actualizado!',
          text: 'El producto ha sido actualizado exitosamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#3085d6',
        });
      } else {
        console.log('➕ Creando producto...');
        await createMutation.mutateAsync(data);
        handleCloseModal();

        // Success message for create
        Swal.fire({
          title: '¡Creado exitosamente!',
          text: 'El producto ha sido creado exitosamente',
          icon: 'success',
          confirmButtonText: 'Aceptar',
          confirmButtonColor: '#3085d6',
        });
      }
      console.log('✅ Producto guardado exitosamente');
    } catch (error: any) {
      console.error('❌ Error al guardar producto:', error);
      const statusCode: number = error?.response?.status || 0;
      const apiMessage: string = error?.response?.data?.message || error?.response?.data?.details || '';
      const lower = apiMessage.toLowerCase();
      const isDuplicate =
        statusCode === 409 ||
        lower.includes('duplicate') ||
        lower.includes('already exists') ||
        lower.includes('ya existe') ||
        lower.includes('conflict') ||
        lower.includes('sku');
      const userMessage = isDuplicate
        ? editingProduct
          ? 'No se pudo actualizar el producto. El código ya está en uso por otro producto. Verifica e intenta con un código diferente.'
          : 'Ya existe un producto con este código. Por favor, utiliza un código diferente.'
        : 'Ocurrió un error al guardar el producto. Por favor, intenta de nuevo.';
      /*
       * Con el asistente activo, el fallo ya se explica en su modal (lo dispara el
       * onError de la mutacion), asi que este Swal quedaba encima pidiendo un clic
       * de mas. Se conserva en dos casos: si el asistente esta apagado (si no, el
       * cliente no veria nada), y si el error es un codigo duplicado, porque ahi el
       * mensaje es especifico y aparece al instante, mientras que el asistente
       * tarda unos segundos en responder.
       */
      if (!assistantEnabled || isDuplicate) Swal.fire({
        title: 'Error',
        text: userMessage,
        icon: 'error',
        confirmButtonText: 'Aceptar',
        confirmButtonColor: '#d33',
      });
    }
  };

  const handleDelete = async (product: Product) => {
    const result = await Swal.fire({
      title: '¿Estás seguro?',
      text: `Se eliminará el producto "${product.name}"`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Sí, eliminar',
      cancelButtonText: 'Cancelar',
    });

    if (result.isConfirmed) {
      await deleteMutation.mutateAsync(product.id);
      Swal.fire({
        title: 'Eliminado',
        text: 'El producto ha sido eliminado exitosamente',
        icon: 'success',
        confirmButtonText: 'Aceptar',
      });
    }
  };

  return (
    <div className="font-display">
      {/* ============ CABECERA ============ */}
      <div className="flex flex-wrap items-end justify-between gap-6">
        <div>
          {/*
            El ultimo segmento del breadcrumb ES el titulo (h1) — sin eyebrow
            arriba (quedaba de mas junto al breadcrumb).
          */}
          <nav className="flex items-baseline gap-2">
            <span className="text-[14px] text-secondary-400">Inicio</span>
            <span className="text-[14px] text-secondary-300">/</span>
            <h1 className="text-2xl font-bold tracking-[-0.03em] text-ink-850 dark:text-white">
              Productos
            </h1>
          </nav>
          <p className="mt-2 text-sm text-secondary-500 dark:text-secondary-400">
            Catálogo de productos y servicios
          </p>
        </div>
        <div className="flex items-center gap-2.5">
          <button type="button" className={GHOST} onClick={() => setIsImportModalOpen(true)} data-testid="product-list-import-button">
            <ArrowUpTrayIcon className="h-3.5 w-3.5" />
            Importar Excel
          </button>
          <button type="button" className={CTA} onClick={handleOpenCreate} data-testid="product-list-new-button">
            <PlusIcon className="h-3.5 w-3.5" />
            Nuevo Producto
          </button>
        </div>
      </div>

      {/*
        ============ FRANJA DE RESUMEN ============
        `total` es el unico dato global que devuelve el endpoint. El mix
        Producto/Servicio, las altas por mes y el precio con impuesto incluido
        salen de los productos de la pagina actual y van rotulados como tales.
      */}
      {!isLoading && !error && products.length > 0 && (
        <div className="mt-6 grid gap-4 lg:grid-cols-[minmax(0,1.7fr)_minmax(0,1fr)]">
          {/* Total + mix Producto / Servicio */}
          <div className={`${CARD} relative overflow-hidden px-6 pb-[22px] pt-[26px] ${TONE_SURFACE.neutral}`}>
            <span aria-hidden="true" className={`absolute inset-x-0 top-0 h-[3px] ${TONE_BAR.neutral}`} />
            <div className="flex items-start justify-between gap-3">
              <div>
                <div className={METRIC_LABEL}>Total productos</div>
                <div className="mt-1.5 flex items-baseline gap-2.5">
                  <span className="text-[40px] font-extrabold leading-none tracking-[-0.035em] text-ink-850 dark:text-white">
                    {data?.total ?? products.length}
                  </span>
                  <span className="font-mono text-[12px] text-secondary-400">
                    {products.length} en esta página
                  </span>
                </div>
              </div>
              <div className="w-[140px] shrink-0">
                <div className="mb-1 text-right text-[11px] font-semibold uppercase tracking-[0.14em] text-secondary-400">
                  Altas por mes · esta página
                </div>
                <Sparkline values={summary.altasByMonth} color="#0d9488" gradientId="spark-productos" />
              </div>
            </div>

            <div className="mt-5">
              <div className="mb-2 text-[11px] font-semibold uppercase tracking-[0.14em] text-secondary-400">
                Producto / Servicio · esta página
              </div>
              <div className="flex h-2.5 items-center gap-[3px] overflow-hidden rounded-full">
                {summary.asProduct > 0 && (
                  <div
                    className="h-full bg-teal-500"
                    style={{ flex: summary.asProduct }}
                    title={`Producto: ${summary.asProduct}`}
                  />
                )}
                {summary.asService > 0 && (
                  <div
                    className="h-full bg-[#6b239e]"
                    style={{ flex: summary.asService }}
                    title={`Servicio: ${summary.asService}`}
                  />
                )}
              </div>
              <div className="mt-2.5 flex flex-wrap items-center gap-x-[22px] gap-y-2 text-[12px] text-secondary-500 dark:text-secondary-400">
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-[2px] bg-teal-500" />
                  Producto <b className="font-bold text-ink-850 dark:text-white">{summary.asProduct}</b>
                </span>
                <span className="inline-flex items-center gap-1.5">
                  <span className="h-2 w-2 rounded-[2px] bg-[#6b239e]" />
                  Servicio <b className="font-bold text-ink-850 dark:text-white">{summary.asService}</b>
                </span>
              </div>
            </div>
          </div>

          {/* Precio con impuesto incluido */}
          <div className={`${CARD} relative overflow-hidden px-6 pb-[22px] pt-[26px] ${TONE_SURFACE.teal}`}>
            <span aria-hidden="true" className={`absolute inset-x-0 top-0 h-[3px] ${TONE_BAR.teal}`} />
            <div className={METRIC_LABEL}>Precio con imp.</div>
            <div className="mt-3.5 flex items-center gap-4">
              <svg viewBox="0 0 60 60" className="h-[58px] w-[58px] shrink-0" aria-hidden="true">
                <circle cx="30" cy="30" r="24" fill="none" stroke="#eef2f6" strokeWidth="6" />
                <circle
                  cx="30"
                  cy="30"
                  r="24"
                  fill="none"
                  stroke="#0d9488"
                  strokeWidth="6"
                  strokeDasharray={ARC_LENGTH}
                  strokeDashoffset={ARC_LENGTH * (1 - taxIncludedPct)}
                  strokeLinecap="round"
                  transform="rotate(-90 30 30)"
                />
              </svg>
              <div>
                <div className="flex items-baseline gap-[3px]">
                  <span className="text-[28px] font-extrabold leading-none tracking-[-0.03em] text-ink-850 dark:text-white">
                    {summary.withTaxIncluded}
                  </span>
                  <span className="text-sm font-semibold text-secondary-400">/{products.length}</span>
                </div>
                <div className="mt-1 text-xs leading-[1.35] text-secondary-500 dark:text-secondary-400">
                  con impuesto incluido
                  <br />
                  <span className="text-secondary-400">en esta página</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ============ BARRA DE FILTRO ============ */}
      <div className="mt-6 flex flex-wrap items-center gap-3.5 border-b border-[#ececec] pb-3.5 dark:border-secondary-700">
        {/* Search Type Selector */}
        <div
          role="group"
          aria-label="Buscar por"
          className="inline-flex shrink-0 overflow-hidden rounded-[10px] border border-[#ececec] dark:border-secondary-600"
        >
          <button
            type="button"
            aria-pressed={searchType === 'name'}
            onClick={() => setSearchType('name')}
            className={`px-[15px] py-2 text-[13px] font-semibold transition-colors ${
              searchType === 'name'
                ? 'bg-ink-850 text-white'
                : 'bg-white text-secondary-500 hover:bg-[#fafafa] hover:text-ink-850 dark:bg-secondary-800 dark:text-secondary-300 dark:hover:bg-secondary-700'
            }`} data-testid="product-list-search-type-name-button"
          >
            Nombre
          </button>
          <button
            type="button"
            aria-pressed={searchType === 'code'}
            onClick={() => setSearchType('code')}
            className={`border-l border-[#ececec] px-[15px] py-2 text-[13px] font-semibold transition-colors dark:border-secondary-600 ${
              searchType === 'code'
                ? 'bg-ink-850 text-white'
                : 'bg-white text-secondary-500 hover:bg-[#fafafa] hover:text-ink-850 dark:bg-secondary-800 dark:text-secondary-300 dark:hover:bg-secondary-700'
            }`} data-testid="product-list-search-type-code-button"
          >
            Código
          </button>
        </div>

        {/* Search Input */}
        <div className="relative max-w-[460px] flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-[13px] top-1/2 h-4 w-4 -translate-y-1/2 text-secondary-400" />
          <input
            type="text"
            placeholder={`Buscar por ${searchType === 'name' ? 'nombre' : 'código'} (mínimo 2 caracteres)...`}
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            className="w-full rounded-[10px] border border-[#ececec] bg-white py-[11px] pl-[38px] pr-[38px] text-[14px] text-ink-850 outline-none transition-colors placeholder:text-secondary-400 focus:border-[#0d9488] focus:ring-[3px] focus:ring-teal-500/[.12] dark:border-secondary-600 dark:bg-secondary-800 dark:text-white"
           data-testid="product-list-search-input"/>
          {searchInput && (
            <button
              type="button"
              onClick={() => setSearchInput('')}
              aria-label="Limpiar búsqueda"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-secondary-400 transition-colors hover:text-ink-850 dark:hover:text-white" data-testid="product-list-search-clear-button"
            >
              <XMarkIcon className="h-4 w-4" />
            </button>
          )}
        </div>

        {/* Search Status Messages */}
        {searchInput.length > 0 && searchInput.length < 2 && (
          <span className="text-[13px] text-secondary-500 dark:text-secondary-400" data-testid="product-list-search-min-chars-message">
            Escribe al menos 2 caracteres para buscar
          </span>
        )}
        {searchInput !== debouncedSearch && searchInput.length >= 3 && (
          <span className="flex items-center gap-1.5 text-[13px] text-secondary-500 dark:text-secondary-400" data-testid="product-list-searching-message">
            <Spinner size="sm" />
            Buscando...
          </span>
        )}
      </div>

      {/* ============ HOJA DE CATÁLOGO ============ */}
      <div className={`${CARD} mt-5 overflow-hidden`}>
        {isLoading && !data ? (
          <div className="flex items-center justify-center py-12">
            <Spinner size="lg" />
          </div>
        ) : error ? (
          <div className="p-5">
            <Alert variant="danger" title="Error al cargar productos" data-testid="product-list-error-alert">
              Ocurrió un error al cargar los productos. Por favor, intenta de nuevo.
            </Alert>
          </div>
        ) : products.length === 0 ? (
          <div className="py-12 text-center">
            {/* Formas geometricas de marca, no icono generico ni ilustracion. */}
            <div className="relative mx-auto mb-5 h-14 w-[76px]" aria-hidden="true">
              <span className="absolute left-0 top-2.5 h-[34px] w-[34px] -rotate-[8deg] rounded-md border-[1.5px] border-secondary-200 dark:border-secondary-600" />
              <span className="absolute right-0 top-1 h-[34px] w-[34px] rounded-md border-[1.5px] border-teal-500/40" />
              <span className="absolute bottom-0 left-[21px] h-5 w-[34px] rounded-md border-[1.5px] border-dashed border-secondary-200 bg-white dark:border-secondary-600 dark:bg-secondary-800" />
            </div>
            <h3 className="text-sm font-bold text-ink-850 dark:text-white">No hay productos</h3>
            <p className="mt-1 text-sm text-secondary-500 dark:text-secondary-400">
              {debouncedSearch ? 'No se encontraron resultados para tu búsqueda' : 'Comienza creando tu primer producto'}
            </p>
            {!debouncedSearch && (
              <div className="mt-6">
                <button type="button" className={CTA} onClick={handleOpenCreate} data-testid="product-list-empty-new-button">
                  <PlusIcon className="h-4 w-4" />
                  Nuevo Producto
                </button>
              </div>
            )}
          </div>
        ) : (
          <>
            {/*
              Scroll horizontal en vez de esconder columnas en pantallas
              angostas: ocultar Tipo / Categoria / Cod. Impuesto seria perder
              informacion de la fila.
            */}
            <div className="overflow-x-auto">
              {/* Cabecera de columnas */}
              <div
                data-testid="product-list-table"
                className={`${ROW_GRID} border-b border-[#f0f0f0] bg-secondary-50 px-[22px] py-3.5 dark:border-secondary-700 dark:bg-secondary-900`}
              >
                <div className={COL_HEAD}>SKU</div>
                <div className={COL_HEAD}>Nombre</div>
                <div className={COL_HEAD}>Tipo · Categoría</div>
                <div className={`${COL_HEAD} pr-2 text-right`}>Precio</div>
                <div className={COL_HEAD}>Código Impuesto</div>
                <div className={COL_HEAD}>Estado</div>
                <div className={`${COL_HEAD} pr-1 text-right`}>Acciones</div>
              </div>

              {products.map((product) => (
                <div
                  key={product.id}
                  data-testid={`product-list-row-${product.id}`}
                  className={`${ROW_GRID} border-t border-dashed border-[#eef0f2] px-[22px] py-3.5 first:border-t-0 ${ROW_HOVER} dark:border-secondary-700`}
                >
                  {/* SKU: se muestra completo. `break-all` porque son
                      identificadores sin espacios: si no, el SKU largo se
                      sale de su celda y se monta sobre Nombre. */}
                  <div
                    className="min-w-0 break-all font-mono text-xs font-bold leading-[1.4] tracking-[0.02em] tabular-nums text-ink-850 dark:text-white"
                    title={product.sku}
                  >
                    {product.sku}
                  </div>

                  {/* Nombre + descripción */}
                  <div className="min-w-0">
                    <div
                      className="truncate text-[14px] font-semibold tracking-[-0.01em] text-ink-850 dark:text-white"
                      title={product.name}
                    >
                      {product.name}
                    </div>
                    {product.description && (
                      <div className="mt-0.5 truncate text-xs text-secondary-500 dark:text-secondary-400">
                        {product.description.substring(0, 50)}
                        {product.description.length > 50 ? '...' : ''}
                      </div>
                    )}
                  </div>

                  {/* Tipo · Categoría: dos columnas del listado original
                      apiladas en una sola celda para ahorrar ancho (misma
                      convencion que "Tienda · Pto" en Facturas emitidas). */}
                  <div className="min-w-0">
                    <span className="inline-flex items-center gap-[7px] whitespace-nowrap text-xs font-semibold text-secondary-500 dark:text-secondary-400">
                      <span
                        className={`h-1.5 w-1.5 shrink-0 rounded-full ${
                          product.isService ? 'bg-[#6b239e]' : 'bg-teal-500'
                        }`}
                      />
                      {product.isService ? 'Servicio' : 'Producto'}
                    </span>
                    <div
                      className="mt-0.5 truncate text-xs text-secondary-400 dark:text-secondary-500"
                      title={product.category || undefined}
                    >
                      {product.category || UNCATEGORIZED}
                    </div>
                  </div>

                  {/* Precio: ver PRICE_CELL. `whitespace-nowrap` para que el
                      importe no se parta; el track admite 12 enteros + 2
                      decimales — antes pisaba Codigo Impuesto. */}
                  <div
                    className={PRICE_CELL}
                    title={`${product.currency || 'USD'} ${product.price != null ? product.price.toFixed(2) : '0.00'}`}
                  >
                    <span className="mr-[5px] font-mono text-[11px] font-bold tracking-[0.06em] text-secondary-300">
                      {product.currency || 'USD'}
                    </span>
                    <span className="text-[15px] font-bold tracking-[-0.02em] tabular-nums text-ink-850 dark:text-white">
                      {product.price != null ? product.price.toFixed(2) : '0.00'}
                    </span>
                    {product.priceIncludesTax && (
                      <span className="mt-0.5 block text-[11px] font-semibold text-secondary-400">
                        (inc. imp.)
                      </span>
                    )}
                  </div>

                  {/* Código Impuesto */}
                  <div
                    className={`font-mono text-xs tabular-nums ${
                      product.taxCode ? 'text-secondary-500 dark:text-secondary-400' : 'text-secondary-300'
                    }`}
                  >
                    {product.taxCode || 'N/A'}
                  </div>

                  {/* Estado: el chip completo queda para el caso excepcional */}
                  <div>
                    {!product.isDeleted ? (
                      <span className="inline-flex items-center gap-[7px] whitespace-nowrap text-xs font-semibold text-secondary-500 dark:text-secondary-400">
                        <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-[#0d9488]" />
                        Activo
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1.5 whitespace-nowrap rounded-full bg-accent-500/[.12] px-2.5 py-1 text-[12px] font-semibold text-[#a15f0a]">
                        <span className="h-[5px] w-[5px] shrink-0 rounded-full bg-accent-500" />
                        Inactivo
                      </span>
                    )}
                  </div>

                  {/* Acciones */}
                  <div className="inline-flex justify-end gap-1">
                    <button
                      type="button"
                      onClick={() => handleOpenEdit(product)}
                      aria-label={`Editar ${product.name}`}
                      title="Editar"
                      className={ICON_BTN} data-testid={`product-list-edit-button-${product.id}`}
                    >
                      <PencilIcon className="h-3.5 w-3.5" />
                    </button>
                    <button
                      type="button"
                      onClick={() => handleDelete(product)}
                      aria-label={`Eliminar ${product.name}`}
                      title="Eliminar"
                      className={ICON_BTN_DANGER} data-testid={`product-list-delete-button-${product.id}`}
                    >
                      <TrashIcon className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </div>
              ))}
            </div>

            {/* ============ RIEL DE RECORRIDO (paginación) ============ */}
            {data && (
              <div className="grid items-center gap-x-7 gap-y-4 border-t border-[#f0f0f0] bg-secondary-50 px-[22px] py-4 dark:border-secondary-700 dark:bg-secondary-900 lg:grid-cols-[auto_minmax(0,1fr)_auto]">
                <div className="text-[13px] text-secondary-500 dark:text-secondary-400">
                  Mostrando{' '}
                  <span className="font-mono font-bold tabular-nums text-ink-850 dark:text-white">
                    {(page - 1) * limit + 1}
                  </span>{' '}
                  a{' '}
                  <span className="font-mono font-bold tabular-nums text-ink-850 dark:text-white">
                    {Math.min(page * limit, data.total)}
                  </span>{' '}
                  de{' '}
                  <span className="font-mono font-bold tabular-nums text-ink-850 dark:text-white">
                    {data.total}
                  </span>{' '}
                  productos
                </div>

                {/* Posicion dentro del catalogo: donde estas, no solo en que pagina */}
                <div>
                  <div className="mb-2 flex items-baseline justify-between">
                    <span className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] tabular-nums text-secondary-400">
                      Página {page} de {totalPages}
                    </span>
                    <span className="font-mono text-[11px] font-bold uppercase tracking-[0.2em] tabular-nums text-secondary-400">
                      {Math.min(page * limit, data.total)} / {data.total}
                    </span>
                  </div>
                  <div className="h-[5px] overflow-hidden rounded-full bg-[#eef2f6] dark:bg-secondary-700">
                    <div
                      className="h-full rounded-full bg-[linear-gradient(90deg,#12c4b2_0%,#3a7ac7_50%,#6b239e_100%)] shadow-[0_0_10px_rgba(18,196,178,.45)] transition-[width] duration-300"
                      style={{
                        width: `${
                          data.total > 0 ? (Math.min(page * limit, data.total) / data.total) * 100 : 0
                        }%`,
                      }}
                    />
                  </div>
                </div>

                <div className="inline-flex gap-2">
                  <button
                    type="button"
                    className={`${PAGE_BTN} gap-1.5 px-3.5`}
                    disabled={page === 1}
                    data-testid="product-list-prev-page-button"
                    onClick={() => setPage(page - 1)}
                  >
                    Anterior
                  </button>
                  <button
                    type="button"
                    className={`${PAGE_BTN} gap-1.5 px-3.5`}
                    disabled={page >= totalPages}
                    data-testid="product-list-next-page-button"
                    onClick={() => {
                      console.log('📄 Clicking next, current page:', page, 'going to:', page + 1);
                      setPage(page + 1);
                    }}
                  >
                    Siguiente
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* Modal Create/Edit */}
      <Modal
        data-testid="product-list-form-modal"
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        kicker="Catálogo · Producto"
        title={editingProduct ? 'Editar Producto' : 'Nuevo Producto'}
        icon={<CubeIcon />}
        badge={<ModalPill tone={editingProduct ? 'active' : 'draft'}>{editingProduct ? 'Edición' : 'Borrador'}</ModalPill>}
        size="4xl"
      >
        <ModalBody>
          <ProductForm
            product={editingProduct || undefined}
            onSubmit={handleSubmit}
            onCancel={handleCloseModal}
            isLoading={createMutation.isPending || updateMutation.isPending}
          />
        </ModalBody>
      </Modal>

      {/* Modal Import Excel */}
      <ProductImportModal
        isOpen={isImportModalOpen}
        onClose={() => setIsImportModalOpen(false)}
        onSuccess={handleImportSuccess}
      />
    </div>
  );
}
